import React, { useState } from 'react';
import axios from 'axios';
import { Lock, UserPlus, MagnifyingGlass, CheckCircle, ArrowLeft, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  name: '',
  date_of_birth: '',
  email: '',
  phone: '',
  service_id: '',
  batch_id: '',
  parent_name: '',
  parent_phone: '',
};

function PublicForm() {
  const [step, setStep] = useState('code'); // code | choose | lookup | form | done
  const [accessCode, setAccessCode] = useState('');
  const [services, setServices] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookupPhone, setLookupPhone] = useState('');
  const [submissionType, setSubmissionType] = useState('new');
  const [existingId, setExistingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/public/verify-code`, { access_code: accessCode.trim() });
      const [svcRes, batchRes] = await Promise.all([
        axios.get(`${API_URL}/public/services`, { params: { access_code: accessCode.trim() } }),
        axios.get(`${API_URL}/public/batches`, { params: { access_code: accessCode.trim() } }),
      ]);
      setServices(svcRes.data);
      setBatches(batchRes.data);
      setStep('choose');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid access code');
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupPhone.trim()) {
      toast.error('Please enter your registered phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/public/lookup`, {
        access_code: accessCode.trim(),
        phone: lookupPhone.trim(),
      });
      if (res.data.found) {
        const s = res.data.student;
        const batch = batches.find((b) => b.id === s.batch_id);
        setExistingId(s.id);
        setSubmissionType('update');
        setForm({
          name: s.name || '',
          date_of_birth: s.date_of_birth || '',
          email: s.email || '',
          phone: s.phone || '',
          service_id: batch?.service_id || '',
          batch_id: s.batch_id || '',
          parent_name: s.parent_name || '',
          parent_phone: s.parent_phone || '',
        });
        setStep('form');
        toast.success(`Welcome back, ${s.name}! Update your details below.`);
      } else {
        toast.info('No record found for that phone number. Please register as a new student.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setSubmissionType('new');
    setExistingId(null);
    setForm(emptyForm);
    setStep('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter the student name'); return; }
    if (!form.batch_id) { toast.error('Please select a batch / class'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/public/submit`, {
        access_code: accessCode.trim(),
        submission_type: submissionType,
        student_id: existingId,
        ...form,
      });
      if (res.data.success) {
        setStep('done');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img src="/mrva-logo.png" alt="MRVA Productions" className="h-14 mx-auto mb-2 object-contain" />
          <p className="text-[11px] font-medium tracking-[0.2em] uppercase text-slate-400 mt-1">Student Registration</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
          {step === 'code' && (
            <form onSubmit={verifyCode} data-testid="public-code-form">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Lock size={20} weight="duotone" className="text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Enter Access Code</h2>
                  <p className="text-xs text-slate-400">Provided by the academy</p>
                </div>
              </div>
              <Input
                data-testid="access-code-input"
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Access code"
                className="bg-slate-950 border-slate-700 text-white mb-4"
                autoFocus
              />
              <Button type="submit" disabled={loading} data-testid="verify-code-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                {loading ? 'Checking...' : 'Continue'}
              </Button>
            </form>
          )}

          {step === 'choose' && (
            <div data-testid="public-choose">
              <h2 className="text-lg font-bold text-white mb-1">Welcome!</h2>
              <p className="text-sm text-slate-400 mb-6">Are you a new student or an existing one?</p>
              <button
                onClick={startNew}
                data-testid="choose-new-btn"
                className="w-full flex items-center gap-4 p-4 mb-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-blue-500 transition-colors text-left"
              >
                <UserPlus size={26} weight="duotone" className="text-blue-400" />
                <div>
                  <p className="font-semibold text-white">New Student</p>
                  <p className="text-xs text-slate-400">Register for the first time</p>
                </div>
              </button>
              <button
                onClick={() => setStep('lookup')}
                data-testid="choose-existing-btn"
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-950 border border-slate-800 hover:border-blue-500 transition-colors text-left"
              >
                <MagnifyingGlass size={26} weight="duotone" className="text-blue-400" />
                <div>
                  <p className="font-semibold text-white">Existing Student</p>
                  <p className="text-xs text-slate-400">Update my information</p>
                </div>
              </button>
            </div>
          )}

          {step === 'lookup' && (
            <form onSubmit={handleLookup} data-testid="public-lookup-form">
              <button type="button" onClick={() => setStep('choose')} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-4">
                <ArrowLeft size={14} /> Back
              </button>
              <h2 className="text-lg font-bold text-white mb-1">Find Your Record</h2>
              <p className="text-sm text-slate-400 mb-5">Enter the phone number registered with the academy.</p>
              <Input
                data-testid="lookup-phone-input"
                type="tel"
                value={lookupPhone}
                onChange={(e) => setLookupPhone(e.target.value)}
                placeholder="Registered phone number"
                className="bg-slate-950 border-slate-700 text-white mb-4"
                autoFocus
              />
              <Button type="submit" disabled={loading} data-testid="lookup-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                {loading ? 'Searching...' : 'Find My Details'}
              </Button>
            </form>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} data-testid="public-student-form">
              <button type="button" onClick={() => setStep('choose')} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-4">
                <ArrowLeft size={14} /> Back
              </button>
              <h2 className="text-lg font-bold text-white mb-1">
                {submissionType === 'update' ? 'Update Your Details' : 'New Registration'}
              </h2>
              <p className="text-sm text-slate-400 mb-5">All submissions are reviewed by the academy before they go live.</p>

              <div className="space-y-4">
                <Field label="Student Name *">
                  <Input data-testid="form-name" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Full name" className="bg-slate-950 border-slate-700 text-white" />
                </Field>
                <Field label="Date of Birth">
                  <Input data-testid="form-dob" type="date" value={form.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} className="bg-slate-950 border-slate-700 text-white" />
                </Field>
                <Field label="Phone Number">
                  <Input data-testid="form-phone" type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="Phone" className="bg-slate-950 border-slate-700 text-white" />
                </Field>
                <Field label="Email">
                  <Input data-testid="form-email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="Email (optional)" className="bg-slate-950 border-slate-700 text-white" />
                </Field>
                <Field label="Service / Academy *">
                  <Select value={form.service_id} onValueChange={(v) => setForm((prev) => ({ ...prev, service_id: v, batch_id: '' }))}>
                    <SelectTrigger data-testid="form-service" className="bg-slate-950 border-slate-700 text-white">
                      <SelectValue placeholder="Select the academy" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Batch / Class *">
                  <Select value={form.batch_id} onValueChange={(v) => setField('batch_id', v)} disabled={!form.service_id}>
                    <SelectTrigger data-testid="form-batch" className="bg-slate-950 border-slate-700 text-white">
                      <SelectValue placeholder={form.service_id ? 'Select your class' : 'Select a service first'} />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.filter((b) => b.service_id === form.service_id).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Parent / Guardian Name">
                  <Input data-testid="form-parent-name" value={form.parent_name} onChange={(e) => setField('parent_name', e.target.value)} placeholder="Parent name" className="bg-slate-950 border-slate-700 text-white" />
                </Field>
                <Field label="Parent / Guardian Phone">
                  <Input data-testid="form-parent-phone" type="tel" value={form.parent_phone} onChange={(e) => setField('parent_phone', e.target.value)} placeholder="Parent phone" className="bg-slate-950 border-slate-700 text-white" />
                </Field>
              </div>

              <Button type="submit" disabled={loading} data-testid="submit-form-btn" className="w-full mt-6 bg-blue-600 hover:bg-blue-500 text-white">
                {loading ? 'Submitting...' : submissionType === 'update' ? 'Submit Update' : 'Submit Registration'}
              </Button>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center py-6" data-testid="public-done">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={36} weight="duotone" className="text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Thank You!</h2>
              <p className="text-sm text-slate-400 mb-6">
                Your details have been submitted and will be reviewed by the academy shortly.
              </p>
              <Button
                onClick={() => { setForm(emptyForm); setExistingId(null); setLookupPhone(''); setStep('choose'); }}
                data-testid="submit-another-btn"
                className="bg-slate-800 hover:bg-slate-700 text-white"
              >
                <Sparkle size={16} weight="duotone" className="mr-2" /> Submit Another
              </Button>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-600 mt-6">MRVA Productions · Kuwait</p>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
    {children}
  </div>
);

export default PublicForm;
